import axios from 'axios';
import CONFIG from '../config';

const api = axios.create({ baseURL: CONFIG.API_BASE_URL });

let isRefreshing = false;
let failedQueue  = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => error ? reject(error) : resolve(token));
  failedQueue = [];
};

const forceLogout = () => {
  localStorage.setItem('sessionExpiredMessage', 'Su sesión ha expirado, por favor ingrese nuevamente');
  ['token', 'refresh_token', 'usuario'].forEach(k => localStorage.removeItem(k));
  setTimeout(() => { window.location.href = '/'; }, 100);
};

const isExpiredOrExpiring = (token, marginSeconds = 300) => {
  if (!token) return true;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000 < Date.now() + marginSeconds * 1000;
  } catch { return true; }
};

const doRefresh = () => {
  const rt = localStorage.getItem('refresh_token');
  if (!rt || isExpiredOrExpiring(rt, 0)) { forceLogout(); return Promise.resolve(null); }
  return axios.post(`${CONFIG.API_BASE_URL}/auth/refresh`, {}, {
    headers: { Authorization: `Bearer ${rt}` },
  }).then(({ data }) => {
    const newToken = data.access_token;
    localStorage.setItem('token', newToken);
    api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    return newToken;
  }).catch(() => { forceLogout(); return null; });
};

// Interceptor request — inyecta token y refresca proactivamente
api.interceptors.request.use(async (config) => {
  let token = localStorage.getItem('token');
  const isRefreshCall = config.url?.includes('/auth/refresh');
  if (token && !isRefreshCall && isExpiredOrExpiring(token)) {
    if (!isRefreshing) {
      isRefreshing = true;
      const newToken = await doRefresh().finally(() => { isRefreshing = false; });
      if (newToken) { processQueue(null, newToken); token = newToken; }
      else { processQueue(new Error('refresh_failed')); return Promise.reject(new Error('refresh_failed')); }
    } else {
      const newToken = await new Promise((resolve, reject) => failedQueue.push({ resolve, reject }));
      token = newToken;
    }
  }
  if (token) config.headers['Authorization'] = `Bearer ${token}`;
  return config;
}, (error) => Promise.reject(error));

// Interceptor response — manejo de 401
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry) {
      const tipo = error.response?.data?.tipo_error;
      if (tipo === 'token_expirado') {
        originalRequest._retry = true;
        if (!isRefreshing) {
          isRefreshing = true;
          const newToken = await doRefresh().finally(() => { isRefreshing = false; });
          if (newToken) { processQueue(null, newToken); originalRequest.headers['Authorization'] = `Bearer ${newToken}`; return api(originalRequest); }
          else { processQueue(new Error('refresh_failed')); }
        } else {
          return new Promise((resolve, reject) => failedQueue.push({ resolve, reject }))
            .then(token => { originalRequest.headers['Authorization'] = `Bearer ${token}`; return api(originalRequest); })
            .catch(err => Promise.reject(err));
        }
      } else if (tipo === 'sesion_invalida') {
        forceLogout();
      }
    }
    return Promise.reject(error);
  }
);

export default api;
